import React, { useState, useEffect, useRef } from "react";
import { View, ScrollView, StyleSheet, Image } from "react-native";
import { ModalM } from "@common/components/modal/ModalM";
import TextM from "@common/components/text/TextM";
import TitleM from "@common/components/text/TitleM";
import FilledButtonM from "@common/components/button/FilledButtonM";
import TextButtonM from "@common/components/button/TextButtonM";
import { theme } from "@common/theme/theme";
import { connect } from "react-redux";
import { apiRequest, apiIdle } from "@data/redux/actions/api";
import { startAvatarGeneration, stopAvatarGeneration } from "@data/redux/actions/data";
import { studentsService } from "@data/services/studentsService";
import { authService } from "@data/services/authService";
import * as ImagePicker from "expo-image-picker";
import { DialogM } from "@common/components/alert/dialog/Dialog";
import { TouchableRipple } from "react-native-paper";
import FontAwesome5 from "react-native-vector-icons/FontAwesome5";
import { useCamera } from "@common/hooks/useCamera";
import { clearLastPhoto } from "@data/redux/actions/camera";
import dayjs from "dayjs";
import "dayjs/locale/fr";


// Helper function to convert image URI to base64 with data URI prefix
const convertImageToBase64 = async (uri) => {
    try {
        // The web camera already hands over a data URI; a photo picked from the
        // gallery is an address to read (a file path on mobile, `blob:` on the web).
        if (uri?.startsWith("data:")) return uri;

        const response = await fetch(uri);
        const blob = await response.blob();
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });
    } catch (error) {
        throw new Error("Erreur lors de la conversion de l'image");
    }
};

const AVATAR_CAMERA_FORM_ID = "avatar_generation";

const GenerateAIAvatarModal = (props) => {
    const { visible, onDismiss, user, cameraPhoto } = props;

    const [selectedImage, setSelectedImage] = useState(null);
    const [generationsRemaining, setGenerationsRemaining] = useState(
        user?.avatar_generations_remaining ?? null
    );

    // Use refs to avoid stale closures in setTimeout and setInterval
    const pollingIntervalRef = useRef(null);
    const pollingTimeoutRef = useRef(null);

    const [launchCamera] = useCamera(AVATAR_CAMERA_FORM_ID, false, false);

    // Update generationsRemaining when user data changes
    useEffect(() => {
        if (user?.avatar_generations_remaining !== undefined) {
            setGenerationsRemaining(user.avatar_generations_remaining);
        }
    }, [user?.avatar_generations_remaining]);

    // Check generation status on mount (handles page refresh)
    useEffect(() => {
        if (user?.id) {
            props.apiRequest(studentsService.getProfileImageStatus, { id: user?.id });
        }
    }, []);

    // Reset state when modal opens/closes
    useEffect(() => {
        if (visible) {
            setSelectedImage(null);
            props.clearLastPhoto();
        } else {
            // Clean up on close ONLY if not generating
            if (pollingIntervalRef.current && !props.isGenerating) {
                stopPolling();
            }
        }
    }, [visible, props.isGenerating]);

    // Watch for camera photo from Redux
    useEffect(() => {
        if (cameraPhoto?.uri && visible) {
            setSelectedImage(cameraPhoto.uri);
            props.clearLastPhoto(); // Clear after using
        }
    }, [cameraPhoto, visible]);

    // Handle image picker from gallery
    const pickImage = async () => {
        try {
            const result = await ImagePicker.launchImageLibraryAsync({
                mediaTypes: ["images"],
                allowsEditing: true,
                aspect: [1, 1],
                quality: 1,
                base64: false, // We'll convert manually for better control
            });

            if (!result.canceled) {
                setSelectedImage(result.assets[0].uri);
            }
        } catch (error) {
            DialogM.show({
                champion: "zoe",
                variant: "unhappy",
                text1: "Erreur",
                text2: "Impossible de sélectionner l'image. Réessaye.",
            });
        }
    };

    // Handle camera to take photo using the app's camera system
    const takePhoto = () => {
        launchCamera();
    };

    // Start polling for avatar generation status
    const startPolling = () => {
        // Clear any existing polling first (but don't call stopAvatarGeneration)
        if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
        }
        if (pollingTimeoutRef.current) {
            clearTimeout(pollingTimeoutRef.current);
            pollingTimeoutRef.current = null;
        }

        // Generation flag already started in handleGenerate
        const interval = setInterval(() => {
            props.apiRequest(studentsService.getProfileImageStatus, { id: user?.id });
        }, 3000);
        pollingIntervalRef.current = interval;

        // Stop polling after 90 seconds timeout (30 attempts × 3 seconds)
        const timeout = setTimeout(() => {
            if (pollingIntervalRef.current) {
                stopPolling();
                DialogM.show({
                    champion: "abou",
                    variant: "main",
                    text1: "Délai dépassé",
                    text2: "La génération prend plus de temps que prévu. Ton avatar apparaîtra bientôt.",
                });
            }
        }, 90000);
        pollingTimeoutRef.current = timeout;
    };

    const stopPolling = () => {
        if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
        }
        if (pollingTimeoutRef.current) {
            clearTimeout(pollingTimeoutRef.current);
            pollingTimeoutRef.current = null;
        }
        props.stopAvatarGeneration();
    };

    // Handle getProfileImageStatus responses
    useEffect(() => {
        if (props.getProfileImageStatusSuccess) {
            const statusData = props.getProfileImageStatusData;
            props.apiIdle(studentsService.getProfileImageStatus);

            const status = statusData?.status;
            const message = statusData?.message;
            const validationReason = statusData?.validation_reason;
            const remaining = statusData?.generations_remaining;

            // Update generations remaining if provided
            if (remaining !== undefined) {
                setGenerationsRemaining(remaining);
            }

            switch (status) {
                case "completed":
                    // Avatar generation completed successfully
                    // Clear polling intervals but keep loader active until new avatar loads
                    if (pollingIntervalRef.current) {
                        clearInterval(pollingIntervalRef.current);
                        pollingIntervalRef.current = null;
                    }
                    if (pollingTimeoutRef.current) {
                        clearTimeout(pollingTimeoutRef.current);
                        pollingTimeoutRef.current = null;
                    }

                    // Update user data to get new avatar
                    // The loader will stop after the new avatar is fetched
                    props.apiRequest(authService.getUser);

                    if (props.isGenerating) {
                        DialogM.show({
                            champion: "sam",
                            variant: "happy",
                            text1: "Avatar prêt !",
                            text2: "Ton nouvel avatar IA a été généré avec succès.",
                        });
                    }
                    break;

                case "validation_failed":
                    // Image validation failed - don't count against limit
                    stopPolling();

                    if (props.isGenerating) {
                        const validationMessages = {
                            'not_a_face': 'Merci de télécharger une vraie photo de ton visage.',
                            'multiple_faces': 'Merci de télécharger une photo avec une seule personne.',
                            'nsfw': 'Cette image n\'est pas appropriée.',
                            'celebrity_risk': 'Nous ne pouvons pas créer d\'avatars de personnalités publiques.',
                            'poor_quality': 'Merci de télécharger une photo plus claire et mieux éclairée.',
                        };

                        const errorMessage = validationMessages[validationReason]
                            || message
                            || "Merci de télécharger une photo valide.";

                        DialogM.show({
                            champion: "zoe",
                            variant: "unhappy",
                            text1: "Image non valide",
                            text2: errorMessage,
                        });
                    }
                    break;

                case "failed":
                    stopPolling();

                    if (props.isGenerating) {
                        DialogM.show({
                            champion: "zoe",
                            variant: "unhappy",
                            text1: "Erreur",
                            text2: message || "Une erreur est survenue lors de la génération. Réessaye.",
                        });
                    }
                    break;

                case "processing":
                    if (!pollingIntervalRef.current) {
                        // Not polling yet (e.g., after page refresh) - start polling
                        props.startAvatarGeneration();
                        startPolling();
                    }
                    // Otherwise, let polling continue
                    break;

                case "idle":
                    stopPolling();

                    if (pollingIntervalRef.current) {
                        console.log("⚠️ Generation no longer in progress - stopped");
                    }
                    break;

                default:
                    stopPolling();

                    if (props.isGenerating) {
                        DialogM.show({
                            champion: "zoe",
                            variant: "unhappy",
                            text1: "Erreur",
                            text2: "Une erreur inattendue est survenue. Réessaye.",
                        });
                    }
            }
        } else if (props.getProfileImageStatusError) {
            props.apiIdle(studentsService.getProfileImageStatus);
            stopPolling();

            // Only show error dialog if we're actively generating
            if (props.isGenerating) {
                DialogM.show({
                    champion: "zoe",
                    variant: "unhappy",
                    text1: "Erreur",
                    text2: "Une erreur s'est produite. Réessaye.",
                });
            }
        }
    }, [props.getProfileImageStatusSuccess, props.getProfileImageStatusError]);

    // Stop avatar generation loader when new user data is fetched
    useEffect(() => {
        if (props.getUserSuccess && props.isGenerating) {
            // New avatar data has been loaded, stop the generation loader
            props.stopAvatarGeneration();
            props.apiIdle(authService.getUser);
        }
    }, [props.getUserSuccess]);

    // Handle generate button press
    const handleGenerate = async () => {
        if (!selectedImage) {
            DialogM.show({
                champion: "abou",
                variant: "main",
                text1: "Image requise",
                text2: "Sélectionne une photo pour générer ton avatar IA",
            });
            return;
        }

        try {
            // Start showing loader on avatar immediately
            props.startAvatarGeneration();

            // Convert image to base64 with data URI prefix
            const base64Image = await convertImageToBase64(selectedImage);

            props.apiRequest(
                studentsService.generateProfileImage,
                { id: user?.id },
                {
                    source_image: base64Image,
                }
            );
        } catch (error) {
            // Stop loader on error
            props.stopAvatarGeneration();
            DialogM.show({
                champion: "zoe",
                variant: "unhappy",
                text1: "Erreur",
                text2: error.message || "Impossible de générer l'avatar. Réessaye.",
            });
        }
    };

    // Handle API response
    useEffect(() => {
        if (props.generateProfileImageSuccess) {
            const responseData = props.generateProfileImageData;
            props.apiIdle(studentsService.generateProfileImage);

            // Update generations remaining from response
            if (responseData?.generations_remaining !== undefined) {
                setGenerationsRemaining(responseData.generations_remaining);
            }

            // Start polling in background
            startPolling();

            // Close the modal first
            onDismiss();

            // Show success message after modal closes
            setTimeout(() => {
                DialogM.show({
                    champion: "sam",
                    variant: "happy",
                    text1: "Génération lancée",
                    text2: "Ton avatar IA sera prêt dans quelques instants.",
                });
            }, 300);
        } else if (props.generateProfileImageError) {
            const error = props.generateProfileImageError;
            props.apiIdle(studentsService.generateProfileImage);

            // Stop loader on error
            props.stopAvatarGeneration();

            // Handle different error scenarios
            if (error.status === 429) {
                DialogM.show({
                    champion: "zoe",
                    variant: "unhappy",
                    text1: "Limite atteinte",
                    text2: error.retry_after
                        ? `Tu as atteint la limite de 3 avatars par mois. Réessaye le mois prochain.`
                        : "Tu as atteint la limite de 3 avatars par mois.",
                });
            } else if (error.status === 403) {
                // Unauthorized
                DialogM.show({
                    champion: "zoe",
                    variant: "unhappy",
                    text1: "Non autorisé",
                    text2: "Tu n'es pas autorisé à générer un avatar pour cet étudiant.",
                });
            } else {
                // Generic error
                DialogM.show({
                    champion: "zoe",
                    variant: "unhappy",
                    text1: "Erreur",
                    text2: error.message || "Une erreur est survenue lors de la génération. Réessaye.",
                });
            }
        }
    }, [props.generateProfileImageSuccess, props.generateProfileImageError]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            stopPolling();
        };
    }, []);

    return (
        <ModalM
            visible={visible}
            onDismiss={onDismiss}
            style={{
                width: "90%",
                maxWidth: 500,
                maxHeight: "90%",
            }}
        >
            <ScrollView
                contentContainerStyle={styles.container}
                showsVerticalScrollIndicator={false}
            >
                <TitleM style={styles.title} wrap>Génère ton avatar IA</TitleM>

                <>
                    {/* Image Selection */}
                    <View style={styles.section}>
                        <TextM fontWeight="Bold" style={styles.sectionTitle} wrap>
                            Prends une photo de ton visage
                        </TextM>

                        <View style={styles.instructionBox}>
                            <FontAwesome5 name="info-circle" size={18} color={theme.colors.primary} />
                            <TextM fontSize={13} style={styles.instructionText} wrap>
                                Prends une photo claire de ton visage seul(e) et de face. Ta photo sera analysée instantanément puis automatiquement supprimée.
                            </TextM>
                        </View>

                        {selectedImage ? (
                            <View style={styles.imagePreviewContainer}>
                                <Image
                                    source={{ uri: selectedImage }}
                                    style={styles.imagePreview}
                                />
                                <TextButtonM
                                    label="Choisir une autre photo"
                                    onPress={pickImage}
                                    style={styles.changeButton}
                                />
                            </View>
                        ) : (
                            <View style={styles.imagePickerOptions}>
                                <TouchableRipple
                                    onPress={takePhoto}
                                    style={[styles.imagePicker, styles.imagePickerHalf]}
                                >
                                    <View style={styles.imagePickerContent}>
                                        <FontAwesome5
                                            name="camera"
                                            size={32}
                                            color={theme.colors.primary}
                                        />
                                        <TextM
                                            fontWeight="Bold"
                                            color={theme.colors.primary}
                                            style={styles.imagePickerText}
                                            wrap
                                        >
                                            Prendre une photo
                                        </TextM>
                                    </View>
                                </TouchableRipple>
                                <TouchableRipple
                                    onPress={pickImage}
                                    style={[styles.imagePicker, styles.imagePickerHalf]}
                                >
                                    <View style={styles.imagePickerContent}>
                                        <FontAwesome5
                                            name="image"
                                            size={32}
                                            color={theme.colors.primary}
                                        />
                                        <TextM
                                            fontWeight="Bold"
                                            color={theme.colors.primary}
                                            style={styles.imagePickerText}
                                            wrap
                                        >
                                            Choisir une photo
                                        </TextM>
                                    </View>
                                </TouchableRipple>
                            </View>
                        )}
                    </View>

                    {/* Action Buttons */}
                    <View style={styles.buttonsContainer}>
                        <FilledButtonM
                            label="Générer avec ma photo"
                            onPress={handleGenerate}
                            loading={props.generateProfileImageRequest}
                            disabled={!selectedImage || props.generateProfileImageRequest || props.isGenerating || generationsRemaining === 0}
                            style={styles.generateButton}
                        />
                        <TextButtonM
                            label="Annuler"
                            onPress={onDismiss}
                            style={styles.cancelButton}
                        />
                    </View>

                    {/* Info */}
                    <View style={styles.infoContainer}>
                        <FontAwesome5
                            name="info-circle"
                            size={16}
                            color={generationsRemaining === 0 || props.isGenerating ? theme.colors.error : theme.colors.primary}
                        />
                        <TextM fontSize={12} style={styles.infoText} wrap>
                            {props.isGenerating
                                ? "Génération en cours - Attends la fin avant d'en lancer une nouvelle"
                                : generationsRemaining !== null
                                    ? generationsRemaining === 0
                                        ? "Limite atteinte - Réessaye le mois prochain"
                                        : `${generationsRemaining} avatar${generationsRemaining > 1 ? 's' : ''} restant${generationsRemaining > 1 ? 's' : ''} ce mois`
                                    : "Limite de 3 avatars par mois"}
                        </TextM>
                    </View>
                </>
            </ScrollView>
        </ModalM>
    );
};

const styles = StyleSheet.create({
    container: {
        padding: 20,
    },
    title: {
        textAlign: "center",
        marginBottom: 20,
        flexWrap: "wrap",
    },
    section: {
        marginBottom: 24,
    },
    sectionTitle: {
        marginBottom: 8,
    },
    sectionDescription: {
        marginBottom: 12,
        color: theme.colors.gray,
    },
    instructionBox: {
        flexDirection: "row",
        alignItems: "flex-start",
        backgroundColor: theme.colors.primaryLighter,
        borderRadius: 12,
        padding: 14,
        marginBottom: 20,
        gap: 12,
    },
    instructionText: {
        color: theme.colors.primary,
        flex: 1,
        lineHeight: 20,
    },
    imagePickerOptions: {
        flexDirection: "row",
        gap: 12,
    },
    imagePicker: {
        borderWidth: 2,
        borderColor: theme.colors.primary,
        borderStyle: "dashed",
        borderRadius: 12,
        padding: 20,
        alignItems: "center",
        justifyContent: "center",
        minHeight: 120,
    },
    imagePickerHalf: {
        flex: 1,
    },
    imagePickerContent: {
        alignItems: "center",
        justifyContent: "center",
        flex: 1,
        paddingHorizontal: 4,
    },
    imagePickerText: {
        textAlign: "center",
        fontSize: 12,
        lineHeight: 16,
        marginTop: 10,
        paddingHorizontal: 4,
    },
    imagePreviewContainer: {
        alignItems: "center",
    },
    imagePreview: {
        width: 150,
        height: 150,
        borderRadius: 75,
        marginBottom: 12,
    },
    changeButton: {
        marginTop: 8,
    },
    buttonsContainer: {
        marginTop: 12,
    },
    generateButton: {
        marginBottom: 12,
    },
    cancelButton: {
        alignSelf: "center",
    },
    infoContainer: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        marginTop: 16,
        padding: 12,
        backgroundColor: theme.colors.primaryLighter,
        borderRadius: 8,
    },
    infoText: {
        marginLeft: 8,
        color: theme.colors.primary,
    },
});

const mapStateToProps = (state) => {
    return {
        user: state.data.currentUser?.object,
        generateProfileImageRequest: state.api.generateProfileImage?.request,
        generateProfileImageSuccess: state.api.generateProfileImage?.success,
        generateProfileImageData: state.api.generateProfileImage?.all_data,
        generateProfileImageError: state.api.generateProfileImage?.error,
        getProfileImageStatusRequest: state.api.getProfileImageStatus?.request,
        getProfileImageStatusSuccess: state.api.getProfileImageStatus?.success,
        getProfileImageStatusData: state.api.getProfileImageStatus?.all_data,
        getProfileImageStatusError: state.api.getProfileImageStatus?.error,
        getUserSuccess: state.api.getUser?.success,
        isGenerating: state.data.currentUser?.avatarGenerating?.isGenerating,
        cameraPhoto: state.camera?.formId === AVATAR_CAMERA_FORM_ID ? state.camera?.lastPhoto : null,
    };
};

const mapDispatchToProps = {
    apiRequest,
    apiIdle,
    startAvatarGeneration,
    stopAvatarGeneration,
    clearLastPhoto,
};

export default connect(mapStateToProps, mapDispatchToProps)(GenerateAIAvatarModal);
